import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiCheckLine, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  Flex,
  InfraCodeBlock,
  InfraDivider,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const DB2CDCInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';
  const db2Host = annotations['db2.infrastructure/host'];
  const db2Port = annotations['db2.infrastructure/port'];
  const db2Database = annotations['db2.infrastructure/database'];
  const db2User = annotations['db2.infrastructure/user'];
  const db2Password = annotations['db2.infrastructure/password'];
  const db2Version = annotations['db2.infrastructure/version'];
  const cdcEnabled = annotations['db2.infrastructure/cdc-enabled'];
  const db2Instance = annotations['db2.infrastructure/instance'];
  const sshCommand = annotations['db2.infrastructure/ssh-command'];
  const sshPrivateKeyEncoded = annotations['db2.infrastructure/ssh-private-key'];

  if (templateType !== 'aws-db2-cdc-source' || !db2Host) return null;

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'db2_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const host = db2Host;
  const port = db2Port || '50000';
  const database = db2Database || 'RDIDB';
  const user = db2User || 'rdi_user';

  const rdiConfig = `connection:
  type: db2
  host: ${host}
  port: ${port}
  database: ${database}
  user: ${user}
  password: ${db2Password || 'RedisCDC2024!'}
advanced:
  source:
    connector.class: io.debezium.connector.db2.Db2Connector
    snapshot.mode: initial`;

  return (
    <InfoCard title="DB2 CDC Source Information" icon={<RiDatabase2Line size={20} />}>
      <InfraStack>
        <InfraSection title="🔵 DB2 Configuration">
          {db2Version && (
            <InfraField
              label="Version:"
              value={<InfraTagValue>{`DB2 ${db2Version}`}</InfraTagValue>}
              mono={false}
            />
          )}
          {cdcEnabled === 'true' && (
            <InfraField
              label="CDC Status:"
              value={
                <InfraTagRow>
                  <InfraTag>
                    <RiCheckLine size={14} style={{ marginRight: 4 }} />
                    Enabled
                  </InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {db2Instance && (
            <InfraField
              label="Instance:"
              value={<InfraTagValue>{db2Instance}</InfraTagValue>}
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="🔌 Connection Information">
          <CopyableValue label="Host:" value={host} />
          <InfraField label="Port:" value={port} />
          <InfraField label="Database:" value={database} />
          <InfraField label="User:" value={user} />
          {db2Password && <CopyableValue label="Password:" value={db2Password} />}
        </InfraSection>

        <InfraDivider />
        <InfraSection title="🚀 RDI Configuration">
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{rdiConfig}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={rdiConfig} />
            </div>
          </div>
        </InfraSection>

        {sshPrivateKey && (
          <>
            <InfraDivider />
            <InfraSection title="🔐 SSH Access">
              {sshCommand && (
                <>
                  <InfraField label="SSH Command:" value="" mono={false} />
                  <div style={{ position: 'relative' }}>
                    <InfraCodeBlock variant="light">{sshCommand}</InfraCodeBlock>
                    <div style={{ position: 'absolute', top: 4, right: 4 }}>
                      <CopyButton text={sshCommand} />
                    </div>
                  </div>
                </>
              )}
              <InfraField label="SSH Private Key:" value="" mono={false} />
              <Flex gap="2">
                <Button variant="primary" size="small" onPress={downloadSSHKey}>
                  <RiDownloadLine size={16} />
                  Download Key
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                >
                  <RiFileCopyLine size={16} />
                  Copy Key
                </Button>
              </Flex>
              <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                💡 Save as db2_key.pem and run: chmod 400 db2_key.pem
              </Text>
            </InfraSection>
          </>
        )}
      </InfraStack>
    </InfoCard>
  );
};
